import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { translate } from 'helpers/translateHelper';
import { SearchBox } from 'sharedComponents/SearchBox';
import { GeneralButton } from 'sharedComponents/GeneralButton';
import { IconSingleAssets } from 'svgIcons/MotionPortalIcons';
import { getAssetIcon } from 'helpers/icons';
import { Checkbox } from 'sharedComponents/Checkbox';
import { useUserCapability } from 'sharedHooks';
import { MAX_INPUT_CHAR_LENGTH } from 'helpers/constants';
import HierarchyAssetMoveModal from './HierarchyAssetMoveModal';
import { ConvertToLocationSuccessBanner } from '../ConvertToLocation';

const HierarchyAssetSelection = (props) => {

    const {
        setPowertrainWizardVisible,
        setWizardSelectedNode,
        selectedAsset,
        selectedIds,
        nodeMap,
        updateHierarchyDataRequest,
        organizations,
        hierarchySelectedAssets,
        setHierarchySelectedAssets,
        hierarchyData,
        getHierarchyDataRequest,
        setHierarchyErrorModal,
        setSaveFailed,
        conversionResult,
        clearConversionResult,
        selectedSites,
    } = props;

    const [searchboxFilter, setSearchboxFilter] = useState('');
    const [showMoveAssetModal, setShowMoveAssetModal] = useState(false);
    const [bannerVisible, setBannerVisible] = useState(false);
    const assetListRef = useRef(null);

    // Get user capabilities for Move Selection button
    const userCapability = useUserCapability();
    const hasRequiredCapabilityForMoveSelection = userCapability?.hasHierarchyConfiguration();

    // Show banner when conversion result arrives for this location's single assets node
    useEffect(() => {
        if (conversionResult?.showBanner && selectedAsset?.locationId === conversionResult?.locationId) {
            setBannerVisible(true);
        } else {
            setBannerVisible(false);
        }
    }, [conversionResult?.showBanner, conversionResult?.locationId, selectedAsset?.locationId]);

    // Clear selected assets, search and reset scroll when the user navigates to a different Single Assets node
    useEffect(() => {
        setHierarchySelectedAssets([]);
        setSearchboxFilter('');
        if (assetListRef.current) {
            assetListRef.current.scrollTop = 0;
        }
    }, [selectedIds?.[0]]);

    // Handler for "Create Powertrain from Selection" button
    const handleCreatePowertrainFromSelection = () => {
        // Wrap selectedAsset as { data: selectedAsset } so extractSiteAndLocationFromNode
        setWizardSelectedNode({
            data: selectedAsset,
            // Extract id (not assetId) from each hierarchy-selected asset for matching with API response
            preSelectedAssetIds: hierarchySelectedAssets.map(asset => asset.id)
        });
        setPowertrainWizardVisible(true);
    };

    const handleCheckboxChange = (asset) => {
        const isAlreadySelected = hierarchySelectedAssets.some(selectedAsset =>
            selectedAsset.id === asset.id
        );

        if (isAlreadySelected) {
            setHierarchySelectedAssets(
                hierarchySelectedAssets.filter(a => a.id !== asset.id)
            );
        } else {
            setHierarchySelectedAssets([...hierarchySelectedAssets, asset]);
        }
    };

    const handleCloseConversionBanner = () => {
        setBannerVisible(false);
        clearConversionResult?.();
    };

    // Filter assets based on search box input
    const filteredAssets = selectedAsset?.assets?.filter(asset => {
        if (!searchboxFilter.trim()) {
            return true;
        }

        const filterValue = searchboxFilter.toLowerCase();
        const assetName = String(asset.name || '').toLowerCase();
        const assetId = String(asset.id || '').toLowerCase();
        const serialNumber = String(asset.serialNumber || '').toLowerCase();

        return assetName.includes(filterValue) ||
            assetId.includes(filterValue) ||
            serialNumber.includes(filterValue);
    }) || [];

    // True when every currently filtered asset is already selected (compares against all assets when search is cleared)
    const areAllFilteredSelected = filteredAssets.length > 0 &&
        filteredAssets.every(fa => hierarchySelectedAssets.some(s => s.id === fa.id));

    const handleSelectAllToggle = () => {
        const filteredIds = new Set(filteredAssets.map(a => a.id));
        if (areAllFilteredSelected) {
            // Unselect only the filtered assets, preserving other existing selections
            setHierarchySelectedAssets(
                hierarchySelectedAssets.filter(a => !filteredIds.has(a.id))
            );
        } else {
            // Select all filtered assets while keeping existing selections
            const existingIds = new Set(hierarchySelectedAssets.map(a => a.id));
            const assetsToAdd = filteredAssets.filter(a => !existingIds.has(a.id));
            setHierarchySelectedAssets([...hierarchySelectedAssets, ...assetsToAdd]);
        }
    };

    // Asset item component for available assets list
    const AvailableAssetItemRenderer = (asset) => {
        const isSelected = hierarchySelectedAssets.some(selectedAsset =>
            selectedAsset.id === asset.id
        );

        return (
            <>
                <div className='asset-checkbox'>
                    <Checkbox
                        id={`available-${asset.id}`}
                        checked={isSelected}
                        onChange={() => handleCheckboxChange(asset)}
                        width='16px'
                        height='16px'
                    />
                </div>
                <div className='asset-icon-container'>
                    {getAssetIcon(asset.assetType, { width: '16px', height: '16px' })}
                </div>
                <div className='asset-info'>
                    <div className='asset-name' title={asset.name}>
                        {asset.name}
                    </div>
                    <div className='asset-id'>
                        ({asset.id})
                    </div>
                </div>
            </>
        );
    };

    return (
        <div className='asset-selection-container'>
            <div className='asset-selection-header'>
                <div className='title'>
                    <IconSingleAssets
                        width={24}
                        height={24}
                    />
                    <h3>{translate('ABB.Powertrain.Frontend.singleAssetsLabel')} ({selectedAsset?.assetCount})</h3>
                </div>
                <div className='asset-options'>
                    {hasRequiredCapabilityForMoveSelection &&
                        <GeneralButton
                            className='move-selection'
                            type='primary'
                            text={translate('ABB.Powertrain.Frontend.moveSelectedLabel')}
                            onClick={() => setShowMoveAssetModal(true)}
                            disabled={!hierarchySelectedAssets?.length}
                        />
                    }
                    <GeneralButton
                        className='create-powertrain'
                        type='normal'
                        text={translate('ABB.Powertrain.Frontend.createPowertrainFromSelectionLabel')}
                        onClick={handleCreatePowertrainFromSelection}
                        disabled={!hierarchySelectedAssets?.length}
                    />
                </div>
            </div>
            <ConvertToLocationSuccessBanner
                isVisible={bannerVisible}
                onClose={handleCloseConversionBanner}
            />
            <div className='search-asset'>
                <SearchBox
                    onChange={setSearchboxFilter}
                    value={searchboxFilter}
                    placeholder={translate('ABB.Powertrain.Frontend.searchHierarchyAssetPlaceholder')}
                    maxLength={MAX_INPUT_CHAR_LENGTH}
                />
                {filteredAssets?.length > 0 &&
                    <GeneralButton
                        className='select-all-button'
                        type='discrete'
                        text={areAllFilteredSelected ? translate('ABB.Powertrain.Frontend.UnselectAllAssets') : translate('ABB.Powertrain.Frontend.SelectAllAssets')}
                        onClick={handleSelectAllToggle}
                    />
                }
            </div>
            <div className='asset-list' ref={assetListRef}>
                {searchboxFilter?.trim() && filteredAssets?.length === 0
                    ? <div className='no-data'>{translate('ABB.Powertrain.Frontend.noSearchResultsLabel')}</div>
                    : filteredAssets.map(asset =>
                        <button
                            className='asset-list-item'
                            key={asset.id}
                            onClick={() => handleCheckboxChange(asset)}
                            type='button'
                            aria-pressed={hierarchySelectedAssets.some(selectedAsset => selectedAsset.id === asset.id)}
                            aria-label={`${hierarchySelectedAssets.some(selectedAsset => selectedAsset.id === asset.id) ? 'Deselect' : 'Select'} asset ${asset.name}`}
                        >
                            {AvailableAssetItemRenderer(asset)}
                        </button>
                    )}
            </div>
            <HierarchyAssetMoveModal
                show={showMoveAssetModal}
                close={() => setShowMoveAssetModal(false)}
                selectedAssets={hierarchySelectedAssets}
                selectedIds={selectedIds}
                nodeMap={nodeMap}
                updateHierarchyDataRequest={updateHierarchyDataRequest}
                organizations={organizations}
                hierarchyData={hierarchyData}
                getHierarchyDataRequest={getHierarchyDataRequest}
                setHierarchyErrorModal={setHierarchyErrorModal}
                setSaveFailed={setSaveFailed}
                setHierarchySelectedAssets={setHierarchySelectedAssets}
                selectedSites={selectedSites}
            />
        </div>
    );
};

HierarchyAssetSelection.propTypes = {
    setPowertrainWizardVisible: PropTypes.func.isRequired,
    setWizardSelectedNode: PropTypes.func.isRequired,
    selectedAsset: PropTypes.object,
    selectedIds: PropTypes.array.isRequired,
    nodeMap: PropTypes.object.isRequired,
    updateHierarchyDataRequest: PropTypes.func.isRequired,
    organizations: PropTypes.array.isRequired,
    hierarchySelectedAssets: PropTypes.array.isRequired,
    setHierarchySelectedAssets: PropTypes.func.isRequired,
    hierarchyData: PropTypes.object,
    selectedSites: PropTypes.array,
    getHierarchyDataRequest: PropTypes.func,
    setHierarchyErrorModal: PropTypes.func,
    setSaveFailed: PropTypes.func,
    conversionResult: PropTypes.object,
    clearConversionResult: PropTypes.func
};

export default HierarchyAssetSelection;
